/* eslint-disable no-undef */
import { getFrameWindow } from 'helpers/iframeHelpers';
import API from 'constants/api';

import { CLOSE_RESOURCE, CLOSE_MODAL, ADD_RESOURCE, SET_FILE, 
  OPEN_MODAL, SET_VALUE, ADD_VALUE, SET_SCROLL_POSITION, SET_SAVE_DISABLED, ADD_STATEMENT_TEXT, ADD_STATEMENT_COMMENT, CAN_LINK_MULTIPLE_OBJECTS } from 'constants/actionTypes';
import { canCloseModal, getActiveResource } from 'helpers/modalHelpers';
import { hideAlert } from './viewActions';
import { loadEnums } from './fieldActions';
import { fetchMetadata, resToJsonHandleErrors } from 'helpers/actionHelpers';
import { getResourceDisplayValue, getResourceFormatChain, saveResource } from './resourceActions';
import { checkUniquenessAndPromptUser } from './validationActions';

import { bindResourceToStore } from 'reducers/store';

/** Opens modal, automatically closed on window unload */
export const openModal = (settings) => dispatch => {
  dispatch({ type: OPEN_MODAL, settings });
  getFrameWindow().addEventListener('pagehide', () => {
    dispatch({type: CLOSE_MODAL});
    dispatch(hideAlert());
  });
};

export const closeModal = () => dispatch => dispatch({ type: CLOSE_MODAL });

/**
 * 1. fetches JSON from UI API
 * 2. binds resource to redux -> opens new breadcrumb for active resource
 * 3. loads enums
 * 
 * @param {*} url API url for JSON
 * @param {*} parentIndex value index, support for minOccurs/maxOccurs
 * @param {*} parentUri ArchivedObject (if only 1 Resource) or previous Resource
 * @returns 
 */
export const addResourceToModal = (url, parentIndex, parentUri) => dispatch => {
  return fetchMetadata(url)
    .then(resource => {
      if (resource.fields) {
        bindResourceToStore({ ...resource, parentUri, parentIndex });
        dispatch(loadEnums(resource.fields));
      } else {
        throw new Error('loadFailed');
      }
    });
};

/**
 * 1. If only 1 resource, closes modal and calls modal.onSave (defined when opening modal)
 * 2. Multiple resources: save active resource
 *    3. Get display value
 *    4. Add display value to previously active resource
 */
export const saveResourceModal = (message) => (dispatch, getState) => {
  const state = getState();
  const resource = getActiveResource(state);
  const isLastResource = canCloseModal(state);
  const archivedObjectUri = state.object.uri;
  const view =  state.object.view;
  const resourceFormatChain = getResourceFormatChain(state.modal.resources);
  const oldFields = state.field.originalValues;

  checkUniquenessAndPromptUser(resource.classUri, resource.fields, oldFields, archivedObjectUri, message)
    .then(allowed => {
      if (allowed) {
        if (isLastResource) {
          dispatch(closeModal());
          return state.modal.onSave(resource, archivedObjectUri, view, resourceFormatChain);
        } else { // still in modal
          return saveResource(resource, archivedObjectUri, view, resourceFormatChain)
            .then(({ value, isCreated }) => {
              if (!(value === '' && isCreated === false)) {
                dispatch({ type: CLOSE_RESOURCE });
                dispatch({
                  type: isCreated ? ADD_VALUE : SET_VALUE,
                  index: resource.parentIndex,
                  predicate: resource.predicate,
                  value
                });
                dispatch(hideAlert());
              }
            });
        }}
    });
};
// Resources may contain a file in formData.
export const setFile = file => dispatch => {
  dispatch({ type: SET_FILE, file });
};



/** Use modal for linking objects with adv search */ 
export const openLinkObjectModal = settings => (dispatch, getState) => {
  const modalIsOpen = getState().modal.showing;
  if (!modalIsOpen) {
    dispatch(openModal(settings));
  } else {
    dispatch({ type: SET_SCROLL_POSITION });
  }
  dispatch({
    type: ADD_RESOURCE,
    index: 0,
    resource: {
      ...settings,
      fields: [],
      addLink: true
    }
  });
};

/* Used when embedding Search into modal:
   on "attach/liitä" click closes the Resource and sets
   display value for linked field.

   Also if predicate allows multiple resources to be linked
   @param objectUris may contain multiple uris. In that case 'save'
   button is enabled and pressing it links all checked resources
   to the current object
*/
export const linkObjects = (objectUris) => (dispatch, getState) => {
  const state = getState();
  const isLastResource = canCloseModal(state);
  const predicate = getActiveResource(state).predicate;
  const uri = state.object.uri;
  const view =  state.object.view ? state.object.view : state.view.view.props.view;
  const resourceFormatChain = getResourceFormatChain(state.modal.resources);
  
  if (Array.isArray(objectUris)) {
    objectUris = objectUris.join(';');
  }

  getResourceDisplayValue(objectUris, predicate, '', uri, view, resourceFormatChain)
    .then(value => {
      if (isLastResource) {
        // Return value to modal opener
        dispatch({ type: CLOSE_MODAL });
        state.modal.onSave(value, uri, view, resourceFormatChain);
      } else {
        dispatch({ type: CLOSE_RESOURCE });
        dispatch({ type: ADD_VALUE, predicate, value });
      }
    });
};


/** Use modal for showing preset statement texts*/ 
export const openStatementTextModal = settings => (dispatch, getState) => {
  const modalIsOpen = getState().modal.showing;

  if (!modalIsOpen) {
    dispatch(openModal(settings));
  } else {
    dispatch({ type: SET_SCROLL_POSITION });
  }
  dispatch({
    type: ADD_STATEMENT_TEXT,
    index: 0,
    resource: {
      ...settings,
      fields: [],
      showStatementPresets: true
    }
  });
};

export const setStatementText = (text) => (dispatch, getState) => {
  const state = getState();
  dispatch({ type: CLOSE_MODAL });
  state.modal.onSave(text);
};


/** Use modal for showing statement comment form*/ 
export const openStatementCommentModal = settings => (dispatch, getState) => {
  const modalIsOpen = getState().modal.showing;

  if (!modalIsOpen) {
    dispatch(openModal(settings));
  } else {
    dispatch({ type: SET_SCROLL_POSITION });
  }
  dispatch({
    type: ADD_STATEMENT_COMMENT,
    index: 0,
    resource: {
      ...settings,
      fields: [],
      showStatementComment: true
    }
  });
};

export const setStatementComment = (text) => (dispatch, getState) => {
  const state = getState();
  dispatch({ type: CLOSE_MODAL });
  state.modal.onSave(text);
};


/* When opening search-view function checks whether multiple objects
   can be linked to the current ArchivedObject instance
*/

export const canLinkMultipleObjects = (predicate) => dispatch => {
  return fetch(API.UI.MAX_OCCURS_UNLIMITED + `&predicate=${predicate}`)
    .then(resToJsonHandleErrors)
    .then((value) => {
      dispatch({ 
        type: CAN_LINK_MULTIPLE_OBJECTS, value 
      });
      return value;
    });
};

export const saveMultipleObjects = (value) => dispatch => {
  dispatch({ type: CAN_LINK_MULTIPLE_OBJECTS, value });
};

/* Modal save can be explicitly disabled, eg. ImageModal requires Image to be set. */
export const setSaveDisabled = saveDisabled => dispatch => {
  dispatch({ type: SET_SAVE_DISABLED, saveDisabled });
};